'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Zap, Mail, List, PlusSquare, Activity, LayoutTemplate, Bot, FileText, NotebookPen, Handshake } from 'lucide-react';
import UserConfigModal from './UserConfigModal';
import { useSession, SectionKey } from '../contexts/SessionContext';
import { createClient } from '../lib/supabase/client';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

function getSectionFromPath(pathname: string): SectionKey | null {
  if (pathname === '/view-inventories' || pathname === '/onboard-inventory') return 'inventories';
  if (pathname === '/intent') return 'signals';
  if (pathname === '/opportunities-screen' || pathname.startsWith('/opportunities-screen/')) return 'opportunities';
  if (pathname === '/reachout-engine') return 'reachout';
  if (pathname === '/pitch-prep') return 'reachout';
  if (pathname === '/media-planner') return 'mediaPlanner';
  if (pathname === '/ai-sales-agent') return 'aiSalesAgent';
  if (pathname === '/rfp-response-engine') return 'rfpEngine';
  return null;
}

export default function Sidebar({ collapsed: externalCollapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [hovered, setHovered] = useState(false);
  const [inboundEnabled, setInboundEnabled] = useState<boolean | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('inbound_enabled');
      if (stored !== null) return stored === 'true';
    }
    return null;
  });
  const { setActiveSection } = useSession();

  // The sidebar is collapsed unless hovered
  const collapsed = !hovered;

  const inboundPaths = ['/media-planner', '/ai-sales-agent', '/rfp-response-engine'];

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'inbound_enabled')
      .single()
      .then(({ data }) => {
        if (data) {
          const val = data.value === 'true';
          setInboundEnabled(val);
          localStorage.setItem('inbound_enabled', String(val));
        } else {
          setInboundEnabled(true);
          localStorage.setItem('inbound_enabled', 'true');
        }
      });

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'inbound_enabled' && e.newValue !== null) {
        const val = e.newValue === 'true';
        setInboundEnabled(val);
        if (!val && inboundPaths.includes(window.location.pathname)) {
          router.push('/opportunities-screen');
        }
      }
    };

    const handleInboundChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ value: boolean }>;
      const val = customEvent.detail.value;
      setInboundEnabled(val);
      if (!val && inboundPaths.includes(window.location.pathname)) {
        router.push('/opportunities-screen');
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('inbound_enabled_changed', handleInboundChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('inbound_enabled_changed', handleInboundChange);
    };
  }, []);

  const isOpportunityActive = pathname === '/opportunities-screen' || pathname.startsWith('/opportunities-screen/');
  const isIntentActive = pathname === '/intent';
  const isReachoutActive = pathname === '/reachout-engine';
  const isPitchPrepActive = pathname === '/pitch-prep';
  const isViewInventoriesActive = pathname === '/view-inventories';
  const isOnboardActive = pathname === '/onboard-inventory';
  const isMediaPlannerActive = pathname === '/media-planner';
  const isAiSalesAgentActive = pathname === '/ai-sales-agent';
  const isRfpResponseActive = pathname === '/rfp-response-engine';
  const isAccountEngagementActive = pathname === '/account-engagement' || pathname.startsWith('/account-engagement/');

  async function handleToggleInbound(value: boolean) {
    setInboundEnabled(value);
    const supabase = createClient();
    await supabase
      .from('app_settings')
      .upsert({ key: 'inbound_enabled', value: String(value), updated_at: new Date().toISOString() });
    if (!value && inboundPaths.includes(pathname)) {
      router.push('/opportunities-screen');
    }
  }

  useEffect(() => {
    const section = getSectionFromPath(pathname);
    setActiveSection(section);
  }, [pathname, setActiveSection]);

  const handleNavClick = () => {
    setHovered(false);
  };

  const navLinkStyle = (isActive: boolean) => ({
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    transition: 'all 0.15s',
    position: 'relative' as const,
    background: isActive ? '#5C00C4' : 'transparent',
    color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
    fontWeight: isActive ? 600 : 400,
    fontFamily: "'Space Grotesk', sans-serif",
    textDecoration: 'none' as const,
  });

  const hoverEnter = (isActive: boolean) => (e: React.MouseEvent<HTMLElement>) => {
    if (!isActive) {
      (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
      (e.currentTarget as HTMLElement).style.color = '#FFFFFF';
    }
  };

  const hoverLeave = (isActive: boolean) => (e: React.MouseEvent<HTMLElement>) => {
    if (!isActive) {
      (e.currentTarget as HTMLElement).style.background = 'transparent';
      (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)';
    }
  };

  return (
    <>
      <aside
        className={`flex flex-col h-full transition-all duration-300 ease-in-out flex-shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ background: '#0A0A0A', borderRight: '1px solid rgba(255,255,255,0.08)' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Logo */}
        <div
          className={`flex items-center h-16 px-4 ${collapsed ? 'justify-center' : 'justify-center'}`}
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}
        >
          {!collapsed && (
            <span style={{ fontWeight: 800, fontSize: '15px', letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: "'Space Grotesk', sans-serif" }}>doohthis</span>
          )}
          {collapsed && (
            <span style={{ fontWeight: 900, fontSize: '16px', color: '#FFFFFF', fontFamily: "'Space Grotesk', sans-serif" }}>d</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-hide">

          {/* Inventories */}
          <Link
            href="/view-inventories"
            title={collapsed ? 'Inventory' : undefined}
            style={navLinkStyle(isViewInventoriesActive)}
            onMouseEnter={hoverEnter(isViewInventoriesActive)}
            onMouseLeave={hoverLeave(isViewInventoriesActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <List size={18} className="flex-shrink-0" />
              : (
                <>
                  <List size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Inventory</span>
                </>
              )
            }
          </Link>

          {/* Outbound section label */}
          {!collapsed && (
            <p className="px-3 pt-4 pb-1.5 text-xs font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em', fontFamily: "'Rethink Sans', sans-serif" }}>
              Outbound
            </p>
          )}
          {collapsed && (
            <div className="my-2 mx-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />
          )}

          {/* Signals */}
          <Link
            href="/intent"
            title={collapsed ? 'Signals' : undefined}
            style={navLinkStyle(isIntentActive)}
            onMouseEnter={hoverEnter(isIntentActive)}
            onMouseLeave={hoverLeave(isIntentActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <Activity size={18} className="flex-shrink-0" />
              : (
                <>
                  <Activity size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Signals</span>
                </>
              )
            }
          </Link>

          {/* Opportunities */}
          <Link
            href="/opportunities-screen"
            title={collapsed ? 'Opportunities' : undefined}
            style={navLinkStyle(isOpportunityActive)}
            onMouseEnter={hoverEnter(isOpportunityActive)}
            onMouseLeave={hoverLeave(isOpportunityActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <Zap size={18} className="flex-shrink-0" />
              : (
                <>
                  <Zap size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Opportunities</span>
                </>
              )
            }
            {collapsed && isOpportunityActive && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: '#5C00C4' }} />
            )}
          </Link>

          {/* Pitch Prep */}
          <Link
            href="/pitch-prep"
            title={collapsed ? 'Pitch Prep' : undefined}
            style={navLinkStyle(isPitchPrepActive)}
            onMouseEnter={hoverEnter(isPitchPrepActive)}
            onMouseLeave={hoverLeave(isPitchPrepActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <NotebookPen size={18} className="flex-shrink-0" />
              : (
                <>
                  <NotebookPen size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Pitch Prep</span>
                </>
              )
            }
          </Link>

          {/* Reachout Engine */}
          <Link
            href="/reachout-engine"
            title={collapsed ? 'Reachout Engine' : undefined}
            style={navLinkStyle(isReachoutActive)}
            onMouseEnter={hoverEnter(isReachoutActive)}
            onMouseLeave={hoverLeave(isReachoutActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <Mail size={18} className="flex-shrink-0" />
              : (
                <>
                  <Mail size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Reachout Engine</span>
                </>
              )
            }
          </Link>

          {/* Account Engagement */}
          <Link
            href="/account-engagement"
            title={collapsed ? 'Account Engagement' : undefined}
            style={navLinkStyle(isAccountEngagementActive)}
            onMouseEnter={hoverEnter(isAccountEngagementActive)}
            onMouseLeave={hoverLeave(isAccountEngagementActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <Handshake size={18} className="flex-shrink-0" />
              : (
                <>
                  <Handshake size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1">Account Engagement</span>
                </>
              )
            }
          </Link>

          {/* Inbound section */}
          {inboundEnabled === true && (
            <>
              {!collapsed && (
                <p className="px-3 pt-4 pb-1.5 text-xs font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em', fontFamily: "'Rethink Sans', sans-serif" }}>
                  Inbound
                </p>
              )}
              {collapsed && (
                <div className="my-2 mx-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />

              )}

              {/* Media Planner Form */}
              <Link
                href="/media-planner"
                title={collapsed ? 'Media Planner Form' : undefined}
                style={navLinkStyle(isMediaPlannerActive)}
                onMouseEnter={hoverEnter(isMediaPlannerActive)}
                onMouseLeave={hoverLeave(isMediaPlannerActive)}
                onClick={handleNavClick}
              >
                {collapsed
                  ? <LayoutTemplate size={18} className="flex-shrink-0" />
                  : (
                    <>
                      <LayoutTemplate size={16} className="flex-shrink-0" />
                      <span className="text-sm font-medium flex-1">Media Planner Form</span>
                    </>
                  )
                }
              </Link>

              {/* AI Sales Agent */}
              <Link
                href="/ai-sales-agent"
                title={collapsed ? 'AI Sales Agent' : undefined}
                style={navLinkStyle(isAiSalesAgentActive)}
                onMouseEnter={hoverEnter(isAiSalesAgentActive)}
                onMouseLeave={hoverLeave(isAiSalesAgentActive)}
                onClick={handleNavClick}
              >
                {collapsed
                  ? <Bot size={18} className="flex-shrink-0" />
                  : (
                    <>
                      <Bot size={16} className="flex-shrink-0" />
                      <span className="text-sm font-medium flex-1">AI Sales Agent</span>
                    </>
                  )
                }
              </Link>

              {/* RFP Response Engine */}
              <Link
                href="/rfp-response-engine"
                title={collapsed ? 'RFP Response Engine' : undefined}
                style={navLinkStyle(isRfpResponseActive)}
                onMouseEnter={hoverEnter(isRfpResponseActive)}
                onMouseLeave={hoverLeave(isRfpResponseActive)}
                onClick={handleNavClick}
              >
                {collapsed
                  ? <FileText size={18} className="flex-shrink-0" />
                  : (
                    <>
                      <FileText size={16} className="flex-shrink-0" />
                      <span className="text-sm font-medium flex-1">RFP Response Engine</span>
                    </>
                  )
                }
              </Link>
            </>
          )}

        </nav>

        {/* Bottom */}
        <div className="py-4 px-2 space-y-0.5" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Create Inventory */}
          <Link
            href="/onboard-inventory"
            title={collapsed ? 'Create Inventory' : undefined}
            style={navLinkStyle(isOnboardActive)}
            onMouseEnter={hoverEnter(isOnboardActive)}
            onMouseLeave={hoverLeave(isOnboardActive)}
            onClick={handleNavClick}
          >
            {collapsed
              ? <PlusSquare size={18} className="flex-shrink-0" />
              : (<><PlusSquare size={16} className="flex-shrink-0" /><span className="text-sm font-medium">Create Inventory</span></>)
            }
          </Link>

          {/* User profile */}
          {!collapsed ? (
            <button
              onClick={() => { router.push('/settings'); handleNavClick(); }}
              className="w-full mt-1 cursor-pointer"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'left',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.09)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.05)'; }}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0" style={{ background: '#5C00C4', color: '#FFFFFF' }}>
                AM
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-xs font-semibold truncate" style={{ color: '#FFFFFF', fontFamily: "'Space Grotesk', sans-serif" }}>Alex Morgan</p>
                <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>Agency plan</p>
              </div>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.3)', flexShrink: 0, letterSpacing: '0.02em' }}>→</span>
            </button>
          ) : (
            <button
              onClick={() => { router.push('/settings'); handleNavClick(); }}
              className="w-full flex items-center justify-center px-3 py-2.5 mt-1 rounded-lg transition-colors cursor-pointer"
              style={{ background: 'transparent', border: 'none' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0" style={{ background: '#5C00C4', color: '#FFFFFF' }}>
                AM
              </div>
            </button>
          )}
        </div>
      </aside>

      <UserConfigModal
        isOpen={false}
        onClose={() => {}}
        inboundEnabled={inboundEnabled}
        onToggleInbound={handleToggleInbound}
      />
    </>
  );
}